import { Injectable, signal, computed } from '@angular/core';
import { createAuthClient } from 'better-auth/client';
import { environment } from '../../environments/environment';

export interface AuthUser {
  id: string;
  email: string;
  name?: string | null;
  image?: string | null;
  emailVerified?: boolean;
}

/** Social providers wired up in auth-service. */
export type SocialProvider = 'google' | 'github';

/** One row of /list-accounts. `providerId` is 'credential' for email+password. */
export interface LinkedAccount {
  id: string;
  providerId: string;
  accountId: string;
  scopes: string[];
}

interface CachedToken {
  jwt: string;
  expiresAtMs: number; // unix ms
}

const TOKEN_REFRESH_LEEWAY_MS = 30_000;

@Injectable({ providedIn: 'root' })
export class AuthService {
  private client = createAuthClient({ baseURL: environment.authServiceUrl });

  private readonly _user = signal<AuthUser | null>(null);
  private readonly _isLoading = signal(true);

  readonly user = this._user.asReadonly();
  readonly isLoading = this._isLoading.asReadonly();
  readonly isAuthenticated = computed(() => this._user() !== null);

  private cachedToken: CachedToken | null = null;
  private inflightTokenFetch: Promise<string | null> | null = null;

  constructor() {
    void this.refreshSession();
  }

  /**
   * Re-read the current session from the auth-service. Updates user signal.
   * Bypasses the session cookieCache so flags changed outside this tab (e.g.
   * emailVerified via the verify link) are fresh on every app load.
   */
  async refreshSession(): Promise<void> {
    this._isLoading.set(true);
    try {
      const { data } = await this.client.getSession({ query: { disableCookieCache: true } });
      this._user.set(data?.user ? this.toAuthUser(data.user) : null);
    } catch {
      this._user.set(null);
    } finally {
      this._isLoading.set(false);
    }
  }

  async signUp(params: { email: string; password: string; name: string }): Promise<{ error?: string }> {
    const { data, error } = await this.client.signUp.email({
      email: params.email,
      password: params.password,
      name: params.name,
    });
    if (error) {
      return { error: error.message ?? 'Sign-up failed' };
    }
    if (data?.user) {
      this._user.set(this.toAuthUser(data.user));
    } else {
      await this.refreshSession();
    }
    this.invalidateToken();
    return {};
  }

  async signIn(params: { email: string; password: string }): Promise<{ error?: string }> {
    const { data, error } = await this.client.signIn.email({
      email: params.email,
      password: params.password,
    });
    if (error) {
      return { error: error.message ?? 'Invalid email or password' };
    }
    if (data?.user) {
      this._user.set(this.toAuthUser(data.user));
    } else {
      await this.refreshSession();
    }
    this.invalidateToken();
    return {};
  }

  /**
   * Hands the browser to the provider's consent screen. On success better-auth
   * redirects to `callbackURL`; on failure to `errorCallbackURL` with an
   * `?error=` code (the login page maps those to copy). Either way this call
   * does not return normally - a resolved error means the redirect never
   * started, e.g. the provider is not configured on this environment.
   */
  async signInWithProvider(
    provider: SocialProvider,
    redirectPath = '/dashboard',
  ): Promise<{ error?: string }> {
    const { error } = await this.client.signIn.social({
      provider,
      callbackURL: `${location.origin}${redirectPath}`,
      errorCallbackURL: `${location.origin}/login`,
    });
    if (error) {
      return { error: error.message ?? 'Sign-in failed. Please try again.' };
    }
    return {};
  }

  /**
   * The accounts linked to this user. A 'credential' entry means they have a
   * password; social-only users have none, which changes what the profile page
   * can offer them.
   */
  async listAccounts(): Promise<{ accounts: LinkedAccount[]; error?: string }> {
    const { data, error } = await this.client.listAccounts();
    if (error) {
      return { accounts: [], error: error.message ?? 'Failed to load linked accounts' };
    }
    return { accounts: (data ?? []) as LinkedAccount[] };
  }

  async updateUser(params: { name: string }): Promise<{ error?: string }> {
    const { error } = await this.client.updateUser({ name: params.name });
    if (error) {
      return { error: error.message ?? 'Failed to update profile' };
    }
    const current = this._user();
    if (current) {
      this._user.set({ ...current, name: params.name });
    }
    return {};
  }

  /**
   * Ask the auth-service to resend the verification email. Uses the signed-in
   * user's address when no email is given (profile page); the login page
   * passes the typed address since there is no session there. The endpoint is
   * enumeration-safe (better-auth reports success for unknown or already
   * verified emails without sending) and server-side throttled (once per day
   * by default) — a 429 comes back with a human-readable message.
   */
  async resendVerificationEmail(emailOverride?: string): Promise<{ error?: string }> {
    const email = emailOverride ?? this._user()?.email;
    if (!email) {
      return { error: 'Enter your email address first.' };
    }
    const { error } = await this.client.sendVerificationEmail({ email });
    if (error) {
      return { error: error.message ?? 'Failed to send verification email' };
    }
    return {};
  }

  /**
   * Ask the auth-service to email a password reset link. The server responds
   * with the same success for unknown emails (no account enumeration), so a
   * missing error here does not mean the account exists.
   */
  async requestPasswordReset(email: string): Promise<{ error?: string }> {
    const { error } = await this.client.requestPasswordReset({
      email,
      redirectTo: `${location.origin}/reset-password`,
    });
    if (error) {
      return { error: error.message ?? 'Failed to request a password reset' };
    }
    return {};
  }

  /** Complete a password reset using the token from the emailed link. */
  async resetPassword(params: { newPassword: string; token: string }): Promise<{ error?: string }> {
    const { error } = await this.client.resetPassword({
      newPassword: params.newPassword,
      token: params.token,
    });
    if (error) {
      return { error: error.message ?? 'Failed to reset password' };
    }
    return {};
  }

  async changePassword(params: { currentPassword: string; newPassword: string }): Promise<{ error?: string }> {
    const { error } = await this.client.changePassword({
      currentPassword: params.currentPassword,
      newPassword: params.newPassword,
      revokeOtherSessions: true,
    });
    if (error) {
      return { error: error.message ?? 'Failed to change password' };
    }
    return {};
  }

  /**
   * Deletes the better-auth account (profile + credentials + sessions).
   * A password makes deletion work regardless of session age; social-only users
   * have none, and better-auth falls back to requiring a "fresh" session
   * (24h) - so it can come back SESSION_EXPIRED, which the caller must handle.
   * Does NOT touch local state: the caller still needs the cached JWT to wipe
   * board data afterwards, then calls clearSession().
   */
  async deleteAccount(params: { password?: string }): Promise<{ error?: string; code?: string }> {
    const { error } = await this.client.deleteUser(
      params.password ? { password: params.password } : {},
    );
    if (error) {
      return { error: error.message ?? 'Failed to delete account', code: error.code };
    }
    return {};
  }

  /** Clear local auth state without a server call (e.g. after account deletion). */
  clearSession(): void {
    this._user.set(null);
    this.invalidateToken();
  }

  async signOut(): Promise<void> {
    try {
      await this.client.signOut();
    } finally {
      this._user.set(null);
      this.invalidateToken();
    }
  }

  /** Returns a JWT for API calls, fetching/refreshing as needed. Null if no session. */
  async getToken(): Promise<string | null> {
    const cached = this.cachedToken;
    if (cached && cached.expiresAtMs - TOKEN_REFRESH_LEEWAY_MS > Date.now()) {
      return cached.jwt;
    }
    if (this.inflightTokenFetch) return this.inflightTokenFetch;

    this.inflightTokenFetch = this.fetchToken().finally(() => {
      this.inflightTokenFetch = null;
    });
    return this.inflightTokenFetch;
  }

  /** Discard the cached JWT (e.g. after a 401). Next getToken() will refetch. */
  invalidateToken(): void {
    this.cachedToken = null;
  }

  private async fetchToken(): Promise<string | null> {
    const res = await fetch(`${environment.authServiceUrl}/api/auth/token`, {
      credentials: 'include',
    });
    if (!res.ok) {
      this.cachedToken = null;
      return null;
    }
    const body = (await res.json()) as { token?: string };
    if (!body.token) {
      this.cachedToken = null;
      return null;
    }
    this.cachedToken = {
      jwt: body.token,
      expiresAtMs: this.readExpFromJwt(body.token) ?? Date.now() + 5 * 60_000,
    };
    return body.token;
  }

  private readExpFromJwt(jwt: string): number | null {
    const [, payload] = jwt.split('.');
    if (!payload) return null;
    try {
      const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
      return typeof json.exp === 'number' ? json.exp * 1000 : null;
    } catch {
      return null;
    }
  }

  private toAuthUser(u: any): AuthUser {
    return {
      id: u.id,
      email: u.email,
      name: u.name ?? null,
      image: u.image ?? null,
      emailVerified: u.emailVerified ?? false,
    };
  }
}
